import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Buildings, FloppyDisk, Robot } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";

export default function BusinessInfo() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/business/profile").then((r) => {
      const d = r.data || {};
      setForm({
        isletme_adi: d.isletme_adi || "", web_sitesi: d.web_sitesi || "", instagram: d.instagram || "",
        faaliyet_alani: d.faaliyet_alani || "", urun_hizmet: d.urun_hizmet || "",
        hedef_musteri: d.hedef_musteri || "",
        hizmet_sehirleri: (d.hizmet_sehirleri || []).join(", "),
        hedef_ulkeler: (d.hedef_ulkeler || []).join(", "),
        rakipler: d.rakipler || "", ek_aciklamalar: d.ek_aciklamalar || "",
      });
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        hizmet_sehirleri: form.hizmet_sehirleri.split(",").map(s => s.trim()).filter(Boolean),
        hedef_ulkeler: form.hedef_ulkeler.split(",").map(s => s.trim()).filter(Boolean),
      };
      await api.post("/business/profile", payload);
      toast.success("İşletme bilgileri güncellendi");
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  if (!form) return <div className="p-6 md:p-10"><Skeleton className="h-96 w-full" /></div>;

  return (
    <div className="p-6 md:p-10 max-w-4xl">
      <div className="mb-6">
        <div className="acas-eyebrow mb-2 flex items-center gap-2"><Buildings size={12} /> Profil</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">İşletme Bilgileri</h1>
        <p className="text-sm text-muted-foreground mt-2">Bu bilgiler işletmenizi tanımlar. Aradığınız ticari ilişki her manuel aramada açık isteğinizden belirlenir.</p>
      </div>

      <div className="rounded-lg border border-border/60 bg-card p-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="İşletme Adı"><Input value={form.isletme_adi} onChange={(e) => set("isletme_adi", e.target.value)} data-testid="bi-isletme" /></Field>
          <Field label="Web Sitesi"><Input value={form.web_sitesi} onChange={(e) => set("web_sitesi", e.target.value)} data-testid="bi-web" /></Field>
          <Field label="Instagram"><Input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} data-testid="bi-instagram" /></Field>
          <Field label="Faaliyet Alanı"><Input value={form.faaliyet_alani} onChange={(e) => set("faaliyet_alani", e.target.value)} data-testid="bi-faaliyet" /></Field>
        </div>
        <Field label="Ürün veya Hizmet"><Textarea rows={3} value={form.urun_hizmet} onChange={(e) => set("urun_hizmet", e.target.value)} data-testid="bi-urun" /></Field>
        <Field label="Hedef Müşteri"><Textarea rows={3} value={form.hedef_musteri} onChange={(e) => set("hedef_musteri", e.target.value)} data-testid="bi-hedef" /></Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Hizmet Verilen Şehirler" hint="Virgülle ayırın"><Input value={form.hizmet_sehirleri} onChange={(e) => set("hizmet_sehirleri", e.target.value)} data-testid="bi-sehirler" /></Field>
          <Field label="Hedef Ülkeler" hint="Virgülle ayırın"><Input value={form.hedef_ulkeler} onChange={(e) => set("hedef_ulkeler", e.target.value)} data-testid="bi-ulkeler" /></Field>
        </div>
        <Field label="Rakipler"><Textarea rows={3} value={form.rakipler} onChange={(e) => set("rakipler", e.target.value)} data-testid="bi-rakipler" /></Field>
        <Field label="Ek Açıklamalar"><Textarea rows={3} value={form.ek_aciklamalar} onChange={(e) => set("ek_aciklamalar", e.target.value)} data-testid="bi-aciklamalar" /></Field>

        <StandingBriefSection />

        <div className="pt-2 flex justify-end">
          <Button onClick={save} disabled={busy} data-testid="save-business" className="gap-2">
            <FloppyDisk size={14} /> {busy ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      {hint && <div className="text-[11px] text-muted-foreground mb-1">{hint}</div>}
      <div className={hint ? "" : "mt-1.5"}>{children}</div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Standing Brief — the instructions Next io uses for AUTONOMOUS daily
// discovery. Without a saved & active brief, the daily loop skips the user
// entirely (no fabricated activity).
// ---------------------------------------------------------------------------
function StandingBriefSection() {
  const [brief, setBrief] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/standing-brief").then((r) => {
      const d = r.data || {};
      setBrief({
        sell_what:       d.sell_what || "",
        ideal_customer:  d.ideal_customer || "",
        industries:      (d.industries || []).join(", "),
        locations:       (d.locations || []).join(", "),
        business_size:   d.business_size || "",
        priorities:      d.priorities || "",
        active:          d.active !== false,
        auto_run_enabled: d.auto_run_enabled === true,
        _exists:         !!(d.sell_what || d.ideal_customer),
      });
    }).finally(() => setLoaded(true));
  }, []);

  const set = (k, v) => setBrief((b) => ({ ...b, [k]: v }));

  const save = async () => {
    if (!brief.sell_what.trim() || !brief.ideal_customer.trim()) {
      toast.error("Ne sattığınız ve ideal müşteriniz zorunludur.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/standing-brief", {
        sell_what: brief.sell_what.trim(),
        ideal_customer: brief.ideal_customer.trim(),
        industries: brief.industries.split(",").map((s) => s.trim()).filter(Boolean),
        locations:  brief.locations.split(",").map((s) => s.trim()).filter(Boolean),
        business_size: brief.business_size || null,
        priorities:    brief.priorities || null,
        active: !!brief.active,
        auto_run_enabled: !!brief.auto_run_enabled,
      });
      setBrief((b) => ({ ...b, _exists: true }));
      toast.success(brief.auto_run_enabled
        ? "Otomatik günlük keşif açıldı. Next io her gün 03:30 UTC'de çalışacak."
        : "Sabit çalışma planı kaydedildi. Otomatik günlük keşif kapalı.");
    } catch (e) {
      toast.error("Sabit çalışma planı kaydedilemedi");
    } finally { setBusy(false); }
  };

  if (!loaded) return <Skeleton className="h-40" />;
  return (
    <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-5 mt-2" data-testid="standing-brief-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Robot size={16} weight="fill" className="text-primary" />
          <h3 className="text-sm font-bold uppercase tracking-wider">Sabit Çalışma Planı</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{brief.auto_run_enabled ? "Otomatik keşif açık" : "Otomatik keşif kapalı"}</span>
          <Switch
            checked={brief.auto_run_enabled}
            onCheckedChange={(v) => set("auto_run_enabled", v)}
            data-testid="automatic-discovery-toggle"
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Otomatik günlük keşif yalnızca siz açtığınızda 03:30 UTC'de çalışır.
        Kapalıyken Next io günlük keşif veya buna bağlı AI/API çağrısı yapmaz.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Ne satıyorsunuz?">
          <Input value={brief.sell_what} onChange={(e) => set("sell_what", e.target.value)}
                 placeholder="Ekşi maya ekmek toptan tedariki" data-testid="brief-sell" />
        </Field>
        <Field label="İdeal müşteriniz">
          <Input value={brief.ideal_customer} onChange={(e) => set("ideal_customer", e.target.value)}
                 placeholder="Semt zincirleri ve kafeler" data-testid="brief-customer" />
        </Field>
        <Field label="Takip edilecek sektörler" hint="Virgülle ayırın">
          <Input value={brief.industries} onChange={(e) => set("industries", e.target.value)}
                 placeholder="kafe, restoran, süpermarket" data-testid="brief-industries" />
        </Field>
        <Field label="Takip edilecek konumlar" hint="Virgülle ayırın">
          <Input value={brief.locations} onChange={(e) => set("locations", e.target.value)}
                 placeholder="İstanbul, İzmir" data-testid="brief-locations" />
        </Field>
        <Field label="İşletme büyüklüğü">
          <select value={brief.business_size} onChange={(e) => set("business_size", e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="brief-size">
            <option value="">Fark etmez</option>
            <option value="small">Küçük</option>
            <option value="medium">Orta</option>
            <option value="enterprise">Büyük</option>
          </select>
        </Field>
        <Field label="Öncelikler">
          <Input value={brief.priorities} onChange={(e) => set("priorities", e.target.value)}
                 placeholder="Yakın konum ve aktif Instagram önemli" data-testid="brief-priorities" />
        </Field>
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={save} disabled={busy} data-testid="save-brief" className="gap-2">
          <FloppyDisk size={14} /> {busy ? "Kaydediliyor…" : (brief._exists ? "Brief'i Güncelle" : "Brief'i Kaydet")}
        </Button>
      </div>
    </div>
  );
}
